export const nodeHtml = `<h1>How do I integrate Prerender.io with Node.js and Express.js?</h1>
<h2>Connect Prerender.io to your Express.js server so AI crawlers and search engines receive fully rendered HTML.</h2>
<div>&nbsp;</div>
<h3>TL;DR</h3>
<div>Install the&nbsp;<code>prerender-node</code>&nbsp;middleware in your Express.js application, add your Prerender.io token, and restart your server. The middleware detects AI crawlers and search engine bots automatically and routes their requests through Prerender.io's rendering service. Your website visitors are not affected.</div>
<div>&nbsp;</div>
<div class="hs-callout-type-warning" data-hs-callout-type="warning">
<p>⚠️ This integration is for Express.js servers that handle all incoming connections, not just API calls. It cannot be installed directly into a React, Angular, or Vue project. If your frontend framework handles routing on its own, see&nbsp;<a href="https://docs.prerender.io/docs/integration-with-react-angular-vue" target="_blank" rel="noopener">how to integrate Prerender.io with a Single Page Application</a>.</p>
</div>
<div class="hs-callout-type-tip" data-hs-callout-type="tip">
<p>ℹ️ Prefer a video walkthrough? A video guide is available at the top of this article.</p>
</div>
<div>&nbsp;</div>
<h4>Video guide:</h4>
<div class="hs-responsive-embed-wrapper hs-responsive-embed" style="width: 100%; height: auto; position: relative; overflow: hidden; padding: 0; max-width: 640px; max-height: 360px; min-width: 256px; margin: 10px auto; display: block; clear: both;">
<div class="hs-responsive-embed-inner-wrapper" style="position: relative; overflow: hidden; max-width: 100%; padding-bottom: 56.25%; margin: 0;"><iframe src="https://player.vimeo.com/video/911179458?share=copy" style="position: absolute; top: 0; left: 0; width: 100%; height: 100%; border: none;"></iframe></div>
</div>
<p>&nbsp;</p>
<h4>Step 1: Log in to your Prerender.io dashboard and copy your token.</h4>
<p>You will need your Prerender.io token for the next step. Log in to your&nbsp;<a href="https://dashboard.prerender.io/" target="_blank" rel="noopener">Prerender.io dashboard</a>&nbsp;and find your token under the&nbsp;<strong>Security and Access</strong>&nbsp;menu.</p>
<p>Copy the token and keep it ready.</p>
<h4>Step 2: Install the&nbsp;<code>prerender-node</code>&nbsp;middleware.</h4>
<p>Open a terminal in your Express.js project directory and run:</p>
<div>
<pre data-file="" data-disable-line-numbers="" data-overflow="wrap"><code data-code="">npm install prerender-node --save</code></pre>
</div>
<p>This adds the&nbsp;<code>prerender-node</code>&nbsp;package to your project. The source code is available on&nbsp;<a href="https://github.com/prerender/prerender-node" target="_blank" rel="noopener">GitHub</a>.</p>
<h4>Step 3: Add the middleware to your Express.js application.</h4>
<p>In your main server file (commonly&nbsp;<code>app.js</code>&nbsp;or&nbsp;<code>server.js</code>), add the following line before any route handlers:</p>
<div>
<div>
<div>
<pre data-file="" data-disable-line-numbers="" data-overflow="wrap"><code data-code=""> app.use(require('prerender-node').set('prerenderToken', 'YOUR_TOKEN'));</code></pre>
<div data-gutter="">&nbsp;</div>
</div>
<div>&nbsp;</div>
</div>
</div>
<p>Replace&nbsp;<code>YOUR_TOKEN</code>&nbsp;with the Prerender.io token you copied in Step 1.</p>
<blockquote>
<p>&nbsp;</p>
</blockquote>
<div class="hs-callout-type-warning" data-hs-callout-type="warning">
<p>⚠️ Do not commit your real token to a public repository. Use an environment variable (e.g.,&nbsp;<code>process.env.PRERENDER_TOKEN</code>) to keep it secure.</p>
</div>
<h4>Step 4: Restart your application.</h4>
<p>Save your changes, then restart your Express.js server so the middleware takes effect:</p>
<div>
<div>
<div>
<pre data-file="" data-disable-line-numbers="" data-overflow="wrap"><code data-code=""> npm start</code></pre>
<div data-gutter="">
<div data-line-type="context" data-column-number="1" data-line-index="0">&nbsp;</div>
</div>
</div>
</div>
</div>
<p>If you use a process manager like&nbsp;<code>pm2</code>:</p>
<div>
<pre data-file="" data-disable-line-numbers="" data-overflow="wrap"><code data-code="">pm2 restart your-app-name </code></pre>
<div data-gutter="">
<div data-line-type="context" data-column-number="1" data-line-index="0">&nbsp;</div>
</div>
</div>
<h4>Step 5: verify your integration is working.</h4>
<p>After restarting, confirm that Prerender.io is intercepting crawler requests and serving rendered&nbsp;<code>HTML</code>.</p>
<ol>
<li>In Chrome DevTools, change your browser's user-agent string to a known crawler (e.g.,&nbsp;<code>Googlebot</code>) and visit one of your pages.</li>
<li>In the&nbsp;<strong>Network</strong>&nbsp;tab, check the response headers for the&nbsp;<code>x-prerender-request-id</code>&nbsp;header. When using the hosted Prerender.io service, this header confirms Prerender.io is serving the page.</li>
<li>Open your Prerender.io dashboard and verify that cached pages begin appearing.</li>
</ol>
<blockquote>
<p>✅ Your integration is working when a simulated bot request returns an&nbsp;<code>x-prerender-request-id</code>&nbsp;response header (hosted Prerender.io service) and cached pages appear in your Prerender.io dashboard.</p>
</blockquote>
<p>For detailed verification steps, see&nbsp;<a href="https://docs.prerender.io/docs/how-to-test-your-site-after-you-have-successfully-validated-your-prerender-integration" target="_blank" rel="noopener">how to verify your Prerender.io integration</a>.</p>
<p>If you see the message&nbsp;<code>Prerender Integration Not Detected</code>, follow the&nbsp;<a href="https://docs.prerender.io/docs/what-should-i-do-if-i-receive-a-prerender-integration-not-detected-error" target="_blank" rel="noopener">troubleshooting guide for integration detection errors</a>.</p>
<p>If the integration works on your homepage but not on other pages, test those pages individually using the same verification steps. Missing Prerender.io headers on specific pages usually means the middleware is not intercepting requests for those routes. Check that the middleware is added before all route handlers in your server file.</p>`;
